"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadDsnTransformComponentGenerator = exports.OrCadDsnTransformComponent = exports.OrCadDsnTransformComponentRequest = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const TransformObject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformObject");
const TransformComponent_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponent");
const TransformComponentSymbol_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponentSymbol");
const TransformImageExtraction_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformImageExtraction");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const orcadLayers_1 = require("../../../orcadLayers");
const orcadShapeConverters_1 = require("../../../orcadShapeConverters");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const OrcadDsnPhysicalParts_1 = require("./OrcadDsnPhysicalParts");
const orcadPinRefs_1 = require("./orcadPinRefs");
const OrcadDsnSectionNumbering_1 = require("./OrcadDsnSectionNumbering");
const OrcadDsnSectionEmission_1 = require("./OrcadDsnSectionEmission");
const orcadDsnPhysicalPartFallback_1 = require("../../orcadDsnPhysicalPartFallback");
const ModelText_1 = require("../../../../../ModelText");
class OrCadDsnTransformComponentRequest extends TransformObject_1.TransformObject {
    constructor(parent, args) {
        const componentName = OrCadDsnTransformComponent.GetComponentName(args);
        const symbolName = OrCadDsnTransformComponent.GetSymbolName(args);
        super(parent, { ...args, componentName, symbolName }, `${componentName}::${symbolName}`, new Set());
        this.dependency = new OrCadDsnTransformComponent(parent, args);
    }
    get componentName() {
        return this._sharedData.componentName;
    }
    get symbolName() {
        return this._sharedData.symbolName;
    }
    get dependencies() {
        return [this.dependency];
    }
    compile() {
        const symbolName = this._sharedData.symbolName;
        const symbol = this.dependency.compiled.symbols.find((s) => s.name === symbolName);
        if (!symbol) {
            throw new Error(`Symbol ${symbolName} not found in component ${this.dependency.compiled.name}`);
        }
        return {
            component: this.dependency.compiled,
            symbol: symbol,
        };
    }
}
exports.OrCadDsnTransformComponentRequest = OrCadDsnTransformComponentRequest;
class OrCadDsnTransformComponent extends TransformComponent_1.TransformComponent {
    constructor(parent, args) {
        const physicalPartSections = args.physicalPartSections && args.physicalPartSections.length > 1 ? args.physicalPartSections : undefined;
        super(parent, {
            ...args,
            baseComponent: {
                name: OrCadDsnTransformComponent.GetComponentName(args),
                ...(physicalPartSections ?
                    {
                        metadata: {
                            'orcad.physicalPart.sectionCount': physicalPartSections.length,
                            'orcad.physicalPart.packages': physicalPartSections
                                .map((s) => `${s.libName}:${s.packageName}`)
                                .sort()
                                .join(', '),
                        },
                    }
                    : {}),
            },
        });
    }
    static GetComponentName(args) {
        if (args.physicalPartSections && args.physicalPartSections.length > 1) {
            return (0, OrcadDsnPhysicalParts_1.getPhysicalPartComponentName)(args.physicalPartSections);
        }
        return `${args.libName}:${args.packageName}`;
    }
    static GetSymbolName(args) {
        return `${args.libPartCellName}:${args.viewName}:${args.viewSuffix}:${args.physicalPartSuffix}`;
    }
    get libName() {
        return this._sharedData.libName;
    }
    get packageName() {
        return this._sharedData.packageName;
    }
    get sourceDesign() {
        return this._sharedData.sourceDesign;
    }
    get packageIndex() {
        return this._sharedData.packageIndex;
    }
    get physicalPartSections() {
        return this._sharedData.physicalPartSections;
    }
}
exports.OrCadDsnTransformComponent = OrCadDsnTransformComponent;
class OrCadDsnTransformComponentGenerator {
    constructor() {
        this.runsFor = [OrCadDsnTransformComponent];
    }
    *generate(parent, _rootContext) {
        const { libName, packageName } = parent;
        const cache = parent.sourceDesign.Cache;
        if (!cache) {
            throw new Error(`No cache found in design for component ${libName}::${packageName}`);
        }
        const sectionCounter = { index: 0, total: this.countTotalSections(parent, cache) };
        const sections = parent.physicalPartSections;
        if (sections && sections.length > 1) {
            const resolutions = sections.map((section) => {
                const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
                if (!resolution) {
                    throw new Error(`Package ${section.libName}::${section.packageName} not found in cache for physical part component ${parent.deduplicationKey}`);
                }
                return resolution;
            });
            const numberingStyle = (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(resolutions[0].pkg.AlphabeticNumbering);
            const emittedPinNumbersByView = new Map();
            for (const resolution of resolutions) {
                for (const libPart of resolution.pkg.LibPart) {
                    yield* this.generateSymbolsFromLibPart(parent, resolution.pkg, libPart, sectionCounter, emittedPinNumbersByView, numberingStyle);
                }
            }
            return;
        }
        const pkg = OrcadDsnUtils_1.OrcadDsnUtils.findPackageByIdentity(cache, libName, packageName, parent.packageIndex);
        if (!pkg) {
            throw new Error(`Package ${libName}::${packageName} not found in cache`);
        }
        for (const libPart of pkg.LibPart) {
            yield* this.generateSymbolsFromLibPart(parent, pkg, libPart, sectionCounter);
        }
    }
    countTotalSections(parent, cache) {
        const sections = parent.physicalPartSections;
        if (sections && sections.length > 1) {
            const libPartsInOrder = [];
            for (const section of sections) {
                const resolution = OrcadDsnUtils_1.OrcadDsnUtils.findBestPackageForPartInst(cache, section.representativePartInst);
                if (!resolution) {
                    continue;
                }
                libPartsInOrder.push(...resolution.pkg.LibPart);
            }
            return (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(libPartsInOrder, 'shared').length;
        }
        const pkg = OrcadDsnUtils_1.OrcadDsnUtils.findPackageByIdentity(cache, parent.libName, parent.packageName, parent.packageIndex);
        if (!pkg) {
            return 0;
        }
        return (0, OrcadDsnSectionEmission_1.enumerateGenuineSections)(pkg.LibPart.map((libPart) => libPart.PhysicalPart?.length ?
            libPart
            : { ...libPart, PhysicalPart: [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)] }), 'perLibPart').length;
    }
    *generateSymbolsFromLibPart(parent, pkg, libPart, sectionCounter, sharedEmittedPinNumbersByView, numberingStyleOverride) {
        const physicalParts = libPart.PhysicalPart?.length ? libPart.PhysicalPart : [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)];
        if (!libPart.PhysicalPart?.length) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-physical-part-missing',
                message: `Synthesized a physical part for ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        const emittedPinNumbersByView = sharedEmittedPinNumbersByView ?? new Map();
        for (const physicalPart of physicalParts) {
            const physicalPartIndex = physicalParts.indexOf(physicalPart);
            const physicalPartSuffix = physicalParts.length > 1 ? `-${physicalPartIndex + 1}` : '';
            const pinPositionMap = (0, OrcadDsnSectionEmission_1.buildPinPositionMap)(physicalPart);
            const pinRefByPosition = (0, orcadPinRefs_1.buildPinRefByPosition)(pinPositionMap);
            const duplicateNumericPins = (0, orcadPinRefs_1.duplicateNumericPinNumbers)(pinPositionMap);
            if (duplicateNumericPins.length > 0) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-duplicate-numeric-pin-number',
                    message: `Duplicate numeric pin number(s) ${duplicateNumericPins.join(', ')} in ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}; Capture refuses these at netlist time (ORCAP-36035/ORCAP-36037).`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            const claimedPinRefsByView = (0, OrcadDsnSectionEmission_1.claimPinsForPhysicalPart)(libPart, pinPositionMap, pinRefByPosition, emittedPinNumbersByView);
            const emitsAnyPin = Array.from(claimedPinRefsByView.values()).some((claimed) => claimed.size > 0);
            const sectionIndex = emitsAnyPin ? sectionCounter.index++ : undefined;
            const signalIdSuffix = sectionIndex !== undefined && sectionCounter.total > 1 ?
                (0, OrcadDsnSectionNumbering_1.orcadDsnSignalIdSectionSuffix)(sectionIndex, numberingStyleOverride ?? (0, OrcadDsnSectionNumbering_1.orcadDsnNumberingStyleFromFlag)(pkg.AlphabeticNumbering))
                : undefined;
            for (const normalView of libPart.NormalView ?? []) {
                const allowedPinRefs = claimedPinRefsByView.get(normalView.Suffix) ?? new Set();
                const { symbol, imageExtractions } = this.generateSymbolFromNormalView(parent, libPart, normalView, physicalPartIndex, physicalPartSuffix, pinPositionMap, pinRefByPosition, allowedPinRefs, signalIdSuffix);
                yield symbol;
                yield* imageExtractions;
            }
        }
    }
    generateSymbolFromNormalView(parent, libPart, normalView, physicalPartIndex, physicalPartSuffix, pinPositionMap, pinRefByPosition, allowedPinRefs, signalIdSuffix) {
        const ref = `${libPart.CellName}${normalView.Suffix}${physicalPartSuffix}`;
        const bboxCenter = [
            (normalView.BoundingBox.X1 + normalView.BoundingBox.X2) / 2,
            (normalView.BoundingBox.Y1 + normalView.BoundingBox.Y2) / 2,
        ];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
        const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(normalView);
        const imageExtractions = bitmapResult.imageExtractions.map((ie) => new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType));
        const drawings = [
            ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(normalView).map((drawing) => ({
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
                drawing,
            })),
            ...bitmapResult.drawings.map((drawing) => ({
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Image].id],
                drawing,
            })),
            ...(normalView.CommentText ?? []).map((c) => ({
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
                drawing: {
                    type: 'text',
                    ...(0, ModelText_1.encodeStrippedText)({ raw: c.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
                    pos: pointTransform([c.LocX, c.LocY]),
                    angle: c.Rotation ?? 0,
                    justify: [-1, 0],
                    size: this.scaleFont(c.Font),
                    thickness: 1,
                },
            })),
        ];
        const pins = [];
        for (const p of [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])]) {
            const pinNumber = pinPositionMap.get(p.Position);
            if (pinNumber === undefined) {
                throw new Error(`Pin position ${p.Position} not found in pin position map`);
            }
            const pinRef = pinRefByPosition.get(p.Position) ?? pinNumber;
            if (!allowedPinRefs.has(pinRef)) {
                continue;
            }
            const pinMetadata = {
                ['orcad.pin.position']: p.Position,
            };
            if (p.IsNoConnect) {
                pinMetadata['orcad.pin.isNoConnect'] = true;
            }
            pins.push({
                type: 'pin',
                ref: pinRef,
                name: p.Name,
                number: pinNumber,
                start: pointTransform([p.StartX, p.StartY]),
                end: pointTransform([p.HotptX, p.HotptY]),
                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
                width: 1,
                drawings: this.generatePinText(p, pinNumber, pointTransform, normalView),
                metadata: pinMetadata,
            });
        }
        const metadata = {
            'orcad.symbol.boundingbox': normalView.BoundingBox,
        };
        if (physicalPartSuffix) {
            metadata['orcad.symbol.physicalPartIndex'] = physicalPartIndex;
        }
        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(parent, {
            ref,
            name: ref,
            pins,
            drawings,
            metadata,
        });
        for (const pin of pins) {
            const signalName = normalView.PinScalar?.find((ps) => ps.Name === pin.name)?.Name ??
                normalView.PinBus?.find((pb) => pb.Name === pin.name)?.Name;
            if (signalName) {
                symbol.setPinSignal(pin, {
                    id: signalIdSuffix ? `${signalName}${signalIdSuffix}` : signalName,
                    name: signalName,
                });
            }
        }
        return { symbol, imageExtractions };
    }
    generatePinText(p, pinRef, pointTransform, view) {
        let nameJustify;
        let angle;
        if (p.StartX > p.HotptX) {
            nameJustify = [-1, 0];
            angle = 0;
        }
        else if (p.StartX < p.HotptX) {
            nameJustify = [1, 0];
            angle = 0;
        }
        else {
            if (p.StartY > p.HotptY) {
                nameJustify = [1, 0];
                angle = 90;
            }
            else {
                nameJustify = [-1, 0];
                angle = 90;
            }
        }
        const textSize = 4;
        const pinNameSource = { raw: p.Name, dialects: [ModelText_1.TextDialect.Orcad] };
        const estNameTextWidth = (0, ModelText_1.displayLength)(pinNameSource) * textSize;
        const nameTextOffset = estNameTextWidth / 2;
        let nameOffset = [0, 0];
        let pinRefOffset = [0, 0];
        const pinNumberPerpOffset = textSize;
        switch (angle) {
            case 0:
                nameOffset = [nameTextOffset * -nameJustify[0], 0];
                pinRefOffset = [0, -pinNumberPerpOffset];
                break;
            case 90:
                nameOffset = [0, nameTextOffset * nameJustify[0]];
                pinRefOffset = [-pinNumberPerpOffset, 0];
                break;
            default:
                break;
        }
        const drawings = [];
        if (view.IsPinNamesVisible) {
            drawings.push({
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinName].id],
                drawing: {
                    type: 'text',
                    ...(0, ModelText_1.encodeStrippedText)(pinNameSource),
                    pos: (0, Transform_1.translatePoint)(pointTransform([p.StartX, p.StartY]), nameOffset),
                    angle,
                    justify: [0, 0],
                    size: textSize,
                    thickness: 1,
                    rejustifyOnRotate: true,
                },
            });
        }
        if (view.IsPinNumbersVisible && p.IsNumberVisible) {
            drawings.push({
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinNumber].id],
                drawing: {
                    type: 'text',
                    ...(0, ModelText_1.encodeStrippedText)({ raw: pinRef, dialects: [ModelText_1.TextDialect.Orcad] }),
                    pos: (0, Transform_1.translatePoint)(pointTransform([(p.StartX + p.HotptX) / 2, (p.StartY + p.HotptY) / 2]), pinRefOffset),
                    angle,
                    justify: [0, 0],
                    size: textSize,
                    thickness: 1,
                    rejustifyOnRotate: true,
                },
            });
        }
        return drawings;
    }
    scaleFont(font) {
        return Math.abs(font.Height / 2);
    }
}
exports.OrCadDsnTransformComponentGenerator = OrCadDsnTransformComponentGenerator;
//# sourceMappingURL=OrcadDsnTransformComponentGenerator.js.map